import platform from 'platform';

type MacArch = 'arm' | 'intel';

// Minimal typings for browser APIs not present in the standard lib.
interface UADataValues {
  architecture?: string;
  platform?: string;
}

interface NavigatorUAData {
  getHighEntropyValues(hints: string[]): Promise<UADataValues>;
}

interface GPUAdapterLike {
  name?: string;
}

interface GPULike {
  requestAdapter(): Promise<GPUAdapterLike | null>;
}

type ExtendedNavigator = Navigator & {
  userAgentData?: NavigatorUAData;
  gpu?: GPULike;
};

export const isSafari = (): boolean => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('safari') && !ua.includes('chrome') && !ua.includes('crios') && !ua.includes('fxios');
};

export const isAppleDevice = (): boolean => {
  const ua = navigator.userAgent || '';
  const navPlatform = navigator.platform || '';

  if (/iPad|iPhone|iPod/.test(ua)) {
    return true;
  }

  if (/Mac/.test(navPlatform)) {
    return true;
  }

  return /Macintosh/.test(ua);
};

export const isMac = (): boolean => {
  const fam = (platform.os?.family || '').toLowerCase();
  return fam.includes('mac') || fam.includes('os x');
};

export const isAppleSiliconByUA = (): boolean => {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  return /arm64|aarch64/i.test(ua);
};

export const detectMacArchWithUAData = async (): Promise<MacArch | null> => {
  const nav = typeof navigator !== 'undefined' ? (navigator as ExtendedNavigator) : undefined;

  if (!nav || !nav.userAgentData || typeof nav.userAgentData.getHighEntropyValues !== 'function') {
    return null;
  }

  try {
    const { architecture, platform: plt } = await nav.userAgentData.getHighEntropyValues(['architecture', 'platform']);
    if ((plt || '').toLowerCase().includes('mac')) {
      return architecture === 'arm' ? 'arm' : 'intel';
    }
  } catch (err) {
    console.error('[MacArchDetect] UA-CH detection failed:', err);
  }
  return null;
};

export const detectMacArchWithGraphics = async (): Promise<MacArch | null> => {
  const nav = typeof navigator !== 'undefined' ? (navigator as ExtendedNavigator) : undefined;

  try {
    if (nav?.gpu?.requestAdapter) {
      const adapter = (await nav.gpu.requestAdapter()) as GPUAdapterLike | null;
      const name = (adapter?.name || '').toLowerCase();
      if (name && (name.includes('apple m') || name.includes('apple gpu'))) {
        return 'arm';
      }
    }
  } catch (err) {
    console.error('[MacArchDetect] WebGPU probe failed:', err);
  }

  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) return null;

    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    const renderer = dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
    const name = (renderer || '').toLowerCase();

    if (name.includes('apple m') || name.includes('apple gpu')) {
      return 'arm';
    }
  } catch (err) {
    console.error('[MacArchDetect] WebGL probe failed:', err);
  }

  return null;
};
